import { gql, useMutation, useQuery } from '@apollo/client'

import {
  ArtistUpdatePreSaleMutation,
  ArtistUpdatePreSaleMutationVariables,
  ConsolePreSaleEditFragmentDoc,
  ConsolePreSaleEditQuery,
  ConsolePreSaleEditQueryVariables,
  PaymentMethod,
  PreSaleType,
} from '@/clients/utoniq-core/schema'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'
import { LotteryEditValues } from './template/LotteryEditForm'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router, generatePath, routerPages } =
    useRouter<{ preSaleId: string; eventId: string }>()
  const { showMessage, showError } = useMessage()
  const { t } = useTranslation('consolePreSaleEdit')
  const { data } = useQuery<
    ConsolePreSaleEditQuery,
    ConsolePreSaleEditQueryVariables
  >(QUERY, {
    variables: {
      id: router.query.preSaleId,
    },
  })
  const preSaleType = data?.artistPreSale?.type

  const [updatePreSale, { loading: submitting }] = useMutation<
    ArtistUpdatePreSaleMutation,
    ArtistUpdatePreSaleMutationVariables
  >(UPDATE_PRESALE)

  return {
    state: {
      preSale: data?.artistPreSale ?? null,
      artistId: user?.uid ?? '',
      submitting,
    },
    actions: {
      async onSubmit(values) {
        const { description, endedAt, isPublished, note, startedAt, title } =
          values

        await updatePreSale({
          variables: {
            id: router.query.preSaleId,
            inputs: {
              description,
              endedAt,
              isPublished,
              note,
              startedAt,
              title,
              itemIds: data?.artistPreSale?.itemIds ?? [],
              paymentMethods:
                values.paymentMethods === 'both'
                  ? [PaymentMethod.CreditCard, PaymentMethod.Konbini]
                  : [values.paymentMethods as PaymentMethod],
              lottery:
                preSaleType === PreSaleType.Lottery
                  ? {
                      applicationEndedAt: (values as LotteryEditValues)
                        .applicationEndedAt,
                      applicationStartedAt: (values as LotteryEditValues)
                        .applicationStartedAt,
                      maxChoice: (values as LotteryEditValues).maxChoice,
                      purchasableCount: (values as LotteryEditValues)
                        .purchasableCount,
                      purchasableEndedAt: values.purchasableEndedAt,
                      purchasableStartedAt: values.purchasableStartedAt,
                      resultNoticedAt: (values as LotteryEditValues)
                        .resultNoticedAt,
                    }
                  : undefined,
              firstComeFirstServed:
                preSaleType === 'first_come_first_served'
                  ? {
                      purchasableCount: values.purchasableCount,
                      purchasableEndedAt: values.purchasableEndedAt,
                      purchasableStartedAt: values.purchasableStartedAt,
                    }
                  : undefined,
            },
          },
        })
          .then(() => {
            showMessage(t('successMessage', '先行販売チケットを更新しました'))
            router.push(
              generatePath(routerPages.ConsoleShokufesEventDetail, {
                eventId: router.query.eventId,
              })
            )
          })
          .catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query ConsolePreSaleEdit($id: ID!) {
    artistPreSale(id: $id) {
      ...ConsolePreSaleEdit
    }
  }
  ${ConsolePreSaleEditFragmentDoc}
`

const UPDATE_PRESALE = gql`
  mutation ArtistUpdatePreSale($id: ID!, $inputs: ArtistUpdatePreSaleInput!) {
    artistUpdatePreSale(id: $id, inputs: $inputs) {
      id
      isPublished
      lottery {
        resultNoticedAt
      }
    }
  }
`
